import { ColorMarker, EditIconButton, TableColumnType } from '@/components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryLookupState, PersonalizationItemState } from '@/redux';
import { array, sort, useBool, useFiltersFactory } from '@/utils';
import {
  CommunityPermissions,
  formatters,
  PricingType,
  SalesTaxType,
} from '@/core';
import { StarFilled } from '@ant-design/icons';
import { Space, Tooltip, Typography } from 'antd';
import { Assert } from '../Communities.Common';
import { PersonalizationTableRowValues } from './PersonalizationDetails';
import styles from './PersonalizationSummary.module.scss';
import { useIsFinalized } from '../Personalization.Details';
import { SpecialRequestStatusTag } from './SpecialRequestStatusTag';
import { EditSpecialRequestButton } from '@/views/Personalization.Details/SpecialRequest/EditSpecialRequestButton';
import { getManualPriceByType, ManualPriceModal } from './ManualPriceModal';

const { Text } = Typography;

function buildPath(path: CategoryLookupState[]): string {
  return path.map((x) => x.name).join(' / ');
}

export function TBDPrice() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary',
  });

  return <Text type="danger">{t('tbd')}</Text>;
}

interface ResidentPriceCellProps {
  item: PersonalizationItemState;
  salesTaxType: SalesTaxType;
  type: PricingType;
}

function PriceCell(props: ResidentPriceCellProps) {
  const { item, salesTaxType, type } = props;
  const { homeId, projectId, step } = item;
  const [modalPriceVisible, , toggleModalPriceVisible] = useBool(false);
  const isFinalized = useIsFinalized(homeId, projectId, step.id);

  if (item.pricing === null) return <></>;

  const value = getManualPriceByType(type, item.pricing, salesTaxType, false);
  const tbd = item.pricing.tbd;

  return (
    <span>
      {value == null || tbd ? <TBDPrice /> : formatters.price(value)}

      {!isFinalized && (
        <Assert
          permission={CommunityPermissions.ResidentPriceOverride.FullAccess}
          active
        >
          <EditIconButton
            className="table-action ms-2"
            onClick={toggleModalPriceVisible}
          />
        </Assert>
      )}
      {modalPriceVisible && (
        <ManualPriceModal
          item={item}
          onClose={toggleModalPriceVisible}
          type={type}
          salesTaxType={salesTaxType}
        />
      )}
    </span>
  );
}

function sortByPrice(
  a: PersonalizationTableRowValues,
  b: PersonalizationTableRowValues,
  select: (x: PersonalizationTableRowValues) => number | undefined,
) {
  const priceA = select(a) ?? 0;
  const priceB = select(b) ?? 0;

  return priceA - priceB;
}

export function usePersonalizationColumns(
  useNumbers: boolean,
  showResidentPrice: boolean,
  showBaseCost: boolean,
  showEditColumn: boolean,
  showOwnerPrice: boolean,
  salesTaxType: SalesTaxType,
  isPublic?: boolean,
) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary',
  });

  const filters = useFiltersFactory(
    array.empty<PersonalizationTableRowValues>(),
  );

  return useMemo<TableColumnType<PersonalizationTableRowValues>[]>(() => {
    const columns: Array<
      TableColumnType<PersonalizationTableRowValues> | false
    > = [
      {
        title: null,
        width: isPublic ? '1%' : '5%',
        key: 'number',
        render: (_, { specialRequest, categoryPath }) =>
          specialRequest != null ? (
            <Tooltip
              placement="right"
              title={t('specialRequestTooltip')}
              className={styles.specialRequestIcon}
            >
              <StarFilled />
            </Tooltip>
          ) : (
            <ColorMarker color={categoryPath[0].hexColor} />
          ),
      },
      useNumbers && {
        title: t('number'),
        width: '5%',
        key: 'number',
        sorter: (a, b) => a.number! - b.number!,
        render: (_, { number }) => number,
        defaultSortOrder: 'ascend',
      },
      !isPublic && {
        title: t('step'),
        key: 'step',
        ...filters.plainText((x) => x.stepName),
        sorter: (a, b) => a.stepName.localeCompare(b.stepName),
        render: (_, { stepName }) => stepName,
      },
      {
        title: t('category'),
        key: 'category',
        width: '10%',
        ...filters.plainText((x) => buildPath(x.categoryPath)),
        sorter: (a, b) => {
          const pathA = buildPath(a.categoryPath);
          const pathB = buildPath(b.categoryPath);

          const comparison = pathA.localeCompare(pathB);

          if (comparison !== 0) {
            return comparison;
          }

          return pathA.length - pathB.length;
        },
        render: (_, { categoryPath }) => buildPath(categoryPath),
        defaultSortOrder: useNumbers ? null : 'ascend',
      },
      {
        title: t('itemName'),
        key: 'itemName',
        width: '10%',
        ...filters.plainText((x) => x.name),
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_, { name }) => name,
        defaultSortOrder: useNumbers ? null : 'ascend',
      },
      {
        title: t('description'),
        key: 'description',
        width: '10%',
        ...filters.plainText((x) => x.description),
        sorter: (a, b) => sort(a.description, b.description),
        render: (_, item) => (
          <div className={styles.description}>{item.description}</div>
        ),
      },
      {
        title: t('notes'),
        key: 'notes',
        width: '10%',
        ...filters.plainText((x) => x.personalizationItem?.notes),
        sorter: (a, b) =>
          sort(a.personalizationItem?.notes, b.personalizationItem?.notes),
        render: (_, item) => (
          <div className={styles.description}>
            {item.personalizationItem?.notes}
          </div>
        ),
      },
      {
        title: t('color'),
        key: 'subOption',
        width: '10%',
        ...filters.plainText((x) => x.personalizationItem?.subOption?.name),
        sorter: (a, b) =>
          sort(
            a.personalizationItem?.subOption?.name,
            b.personalizationItem?.subOption?.name,
          ),
        render: (_, item) => (
          <Space>
            {item.personalizationItem?.subOption?.name}
            {item.specialRequest && showEditColumn && (
              <EditSpecialRequestButton
                value={item.specialRequest}
                homeId={item.specialRequest.homeId}
                projectId={item.specialRequest.projectId}
                stepId={item.specialRequest.step.id}
                isTableAction
              />
            )}
          </Space>
        ),
        className: 'text-end',
      },
      showBaseCost &&
        !isPublic && {
          title: t('baseCost'),
          key: 'baseCost',
          width: '10%',
          ...filters.numberRange((x) => x.totalBaseCost),
          sorter: (a, b) => sortByPrice(a, b, (x) => x.totalBaseCost),
          render: (_, item) => (
            <div>
              {item.personalizationItem ? (
                <PriceCell
                  item={item.personalizationItem}
                  type="Base"
                  salesTaxType={salesTaxType}
                />
              ) : (
                <Space>
                  {formatters.price(item.totalBaseCost) ??
                    (!item.isExistingItem && <TBDPrice />)}
                </Space>
              )}
            </div>
          ),
          className: 'text-end',
        },
      showOwnerPrice &&
        !isPublic && {
          title: t('ownerPrice'),
          key: 'ownerPrice',
          width: '10%',
          ...filters.numberRange((x) => x.totalOwnerPrice),
          sorter: (a, b) => sortByPrice(a, b, (x) => x.totalOwnerPrice),
          render: (_, item) => (
            <div>
              {item.personalizationItem ? (
                <PriceCell
                  item={item.personalizationItem}
                  type="Owner"
                  salesTaxType={salesTaxType}
                />
              ) : (
                <Space>
                  {formatters.price(item.totalOwnerPrice) ??
                    (!item.isExistingItem && <TBDPrice />)}
                </Space>
              )}
            </div>
          ),
          className: 'text-end',
        },
      showResidentPrice &&
        !isPublic && {
          title: t('residentPrice'),
          key: 'residentPrice',
          width: '12%',
          ...filters.numberRange((x) => x.totalResidentPrice),
          sorter: (a, b) => sortByPrice(a, b, (x) => x.totalResidentPrice),
          render: (_, item) => (
            <div>
              <div>
                {item.personalizationItem ? (
                  <PriceCell
                    item={item.personalizationItem}
                    salesTaxType={salesTaxType}
                    type="Resident"
                  />
                ) : (
                  <Space>
                    {item.totalResidentPrice !== null
                      ? formatters.price(
                          salesTaxType === SalesTaxType.ItemLevel
                            ? item.totalResidentPrice
                            : item.specialRequest?.prices
                                ?.residentPriceWithoutTax,
                        )
                      : !item.isExistingItem && <TBDPrice />}
                    {item.specialRequest && (
                      <EditSpecialRequestButton
                        value={item.specialRequest}
                        homeId={item.specialRequest.homeId}
                        projectId={item.specialRequest.projectId}
                        stepId={item.specialRequest.step.id}
                        isTableAction
                      />
                    )}
                  </Space>
                )}
              </div>
              <div>
                <SpecialRequestStatusTag item={item.specialRequest} />
              </div>
            </div>
          ),
          className: 'text-end',
        },
    ];

    return columns.filter(
      (x) => !!x,
    ) as TableColumnType<PersonalizationTableRowValues>[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    t,
    useNumbers,
    showBaseCost,
    showResidentPrice,
    showEditColumn,
    showOwnerPrice,
    salesTaxType,
  ]);
}
